/* ==========================================================================
   Cart Rewards - collapse the reward cards while the cart list is scrolled
   --------------------------------------------------------------------------
   The reward cards sit ABOVE the drawer's scrolling item list rather than
   inside it, so they never scroll away. Good for visibility, but on a phone
   the two cards were taking about 240px of a ~660px drawer, leaving very
   little room for the products themselves.

   Once the customer scrolls the item list we mark the stack collapsed and
   assets/cart-rewards.css + assets/cart-tier.css fold each card down - message
   and progress bar only. Scrolling back to the top restores them. The CSS
   applies the collapse on small screens only; a desktop drawer has the room,
   and folding cards away under someone's cursor would just be startling.

   TWO THINGS THIS FILE HAS TO BE CAREFUL ABOUT
   --------------------------------------------------------------------------
   1. Collapsing GIVES the list back height. If the list only just overflowed,
      collapsing can remove the overflow entirely - the browser then clamps
      scrollTop back to 0, we see 0, we un-collapse, and the list springs back
      to the top. To the customer the drawer simply refuses to scroll. So:
      collapse, measure, and if the customer's scroll position no longer fits,
      put it straight back and stop trying for this cart. Measured repro:
      390x660 with 2 products, scrollTop 60 of a possible 138 -> settled at 0.

   2. Below 700px of viewport height the theme moves the scrollbar: the item
      list goes `overflow: visible` and `.cart-drawer__inner` becomes the
      scroller (component-cart-drawer.css). There the cards scroll out of view
      on their own, so collapsing them buys nothing and only risks (1). We sit
      that case out entirely.

   Deliberately standalone and dependency-free:

   - One capture-phase scroll listener on the document. Scroll events don't
     bubble, but they DO capture, so this survives the theme re-rendering the
     drawer wholesale on every cart update - which it does, and which would
     defeat a listener bound directly to the scrolling element.
   - No reliance on CartRewardsCore, so load order doesn't matter.
   - If anything is missing it simply does nothing, and the cards behave
     exactly as they did before.
   ========================================================================== */
(function () {
  'use strict';

  var COLLAPSE_AT = 24; /* px of scroll before the cards fold */
  var EXPAND_AT = 2; /* effectively "back at the top" */
  var SAFETY = 4; /* px of headroom, the cards' padding transition is ~3px */
  var ATTR = 'data-cart-rewards-collapsed';
  var DRAWER_SELECTOR = 'cart-drawer, .cart-drawer, .drawer--cart, #CartDrawer';
  var LIST_SELECTOR = 'cart-drawer-items, .cart-drawer__items, .drawer__cart-items';

  /* Set once the verify step has shown that collapsing this particular cart
     would cost the customer their scroll position. Cleared whenever the cart
     re-renders or the viewport changes, i.e. whenever the sums change. */
  var blocked = false;

  var pending = false;
  var queued = null;

  function maxScroll(el) {
    return el.scrollHeight - el.clientHeight;
  }

  function apply() {
    pending = false;
    var job = queued;
    queued = null;
    if (!job) return;

    var stack = job.stack;
    var scroller = job.scroller;
    if (!stack || !stack.isConnected || !scroller || !scroller.isConnected) return;

    var collapsed = stack.getAttribute(ATTR) === 'true';
    var top = scroller.scrollTop;

    if (!collapsed) {
      if (blocked || top <= COLLAPSE_AT) return;

      /* Collapse, then check the customer can still be where they are. Reading
         scrollHeight forces the layout, so this is measured, not predicted. */
      stack.setAttribute(ATTR, 'true');
      if (maxScroll(scroller) < top + SAFETY) {
        stack.setAttribute(ATTR, 'false');
        scroller.scrollTop = top; /* the read above may have clamped it */
        blocked = true;
      }
      return;
    }

    /* Only unfold once they are genuinely back at the top. Anything looser and
       a clamp reads as "scrolled to the top" and we start flip-flopping. */
    if (top <= EXPAND_AT) stack.setAttribute(ATTR, 'false');
  }

  function schedule(stack, scroller) {
    queued = { stack: stack, scroller: scroller };
    if (pending) return;
    pending = true;
    if (window.requestAnimationFrame) window.requestAnimationFrame(apply);
    else setTimeout(apply, 60);
  }

  function onScroll(event) {
    var scroller = event.target;
    if (!scroller || scroller.nodeType !== 1 || !scroller.closest) return;

    /* Only the cart drawer. The page itself scrolling is not our business. */
    var drawer = scroller.closest(DRAWER_SELECTOR);
    if (!drawer) return;

    /* Only when the ITEM LIST is the thing scrolling. On short viewports the
       theme makes the whole drawer panel the scroller and the cards scroll
       away by themselves - see note 2 at the top. */
    if (!scroller.matches(LIST_SELECTOR)) return;

    var stack = drawer.querySelector('.cart-rewards-stack');
    if (!stack) return;

    schedule(stack, scroller);
  }

  /* Capture phase is the whole trick - see the note at the top of the file. */
  try {
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  } catch (e) {
    /* Older browsers without options-object support. */
    document.addEventListener('scroll', onScroll, true);
  }

  function reset() {
    blocked = false;
    var stacks = document.querySelectorAll('.cart-rewards-stack[' + ATTR + "='true']");
    for (var i = 0; i < stacks.length; i++) stacks[i].setAttribute(ATTR, 'false');
  }

  /* A freshly re-rendered drawer starts at scrollTop 0, so clear any stale
     collapsed state rather than leaving the cards folded with nothing
     scrolled - and re-open the question of whether collapsing is worth it,
     because the cart just changed size. Same on resize/rotate. */
  document.addEventListener('shopify:section:load', reset);
  window.addEventListener('resize', reset);
  window.addEventListener('orientationchange', reset);
})();

/* ==========================================================================
   Cart Rewards - free-gift SIZE PICKER  (append to collapse module)
   --------------------------------------------------------------------------
   Adds variant (size) selection to the free-gift card when, and only when, a
   gift product has more than one variant. Single-variant gifts (e.g. socks)
   are left completely untouched - the existing Claim / auto-add path handles
   them exactly as before, so this block is a no-op for the current gift pool.

   Why it lives here: the gift markup is rendered inside a <template> in
   sections/cart-rewards-ladder.liquid and only becomes live DOM once
   cart-rewards.js clones it into the drawer / cart page, so the behaviour has
   to run from a loaded asset. This module is already loaded and already
   watches the same DOM, so the size picker rides along rather than adding a
   fourth <script> tag (which would mean editing the section).

   How it works, kept deliberately self-contained:
   - Reads each gift product's variants from Shopify's own /products/{handle}.js
     (cached), so no theme file has to render variant data.
   - Injects its own <style> and a .arl-sizes chip row into the featured card,
     using the card's existing --arl-* tokens so it matches every theme.
   - For a multi-variant gift, Claim is intercepted in the capture phase: the
     first tap reveals the sizes, and once a size is chosen the tap adds THAT
     variant via window.__arlInstance.addGift(). The collection-scoped gift
     discount zeroes any variant of a gift product, so the chosen size checks
     out free.
   - Auto-add is suppressed only while the featured gift is multi-variant, so
     the cart never silently adds a size the customer did not choose.
   - Everything is wrapped so a failure can only mean "no size picker", never a
     broken cart.
   ========================================================================== */
(function () {
  'use strict';

  var STYLE_ID = 'arl-sizes-style';
  var cache = {};      /* handle -> array of {id,title,available} */
  var pending = {};    /* handle -> true while a fetch is in flight */
  var applying = false;
  var rafId = null;

  function copy(key, fallback) {
    try {
      var c = window.__arlInstance && window.__arlInstance.cfg && window.__arlInstance.cfg.copy;
      return (c && c[key]) || fallback;
    } catch (e) {
      return fallback;
    }
  }

  function unlockedState() {
    try {
      var s = window.__arlInstance && window.__arlInstance.lastState;
      return { unlocked: !!(s && s.giftUnlocked), inCart: !!(s && s.giftInCart) };
    } catch (e) {
      return { unlocked: false, inCart: false };
    }
  }

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var css =
      '.arl-sizes{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px;}' +
      '.arl-sizes[hidden]{display:none;}' +
      ".arl-sizes__label{flex:0 0 100%;font-size:max(9px,calc(var(--arl-font-size) - 3px));letter-spacing:var(--arl-letter-spacing);text-transform:var(--arl-transform);color:var(--arl-text-2);margin-bottom:1px;}" +
      '.arl-size{appearance:none;-webkit-appearance:none;font-family:inherit;font-size:max(10px,calc(var(--arl-font-size) - 1px));line-height:1;padding:6px 10px;cursor:pointer;background:transparent;color:var(--arl-text);border:1px solid var(--arl-marker-upcoming);border-radius:calc(var(--arl-radius-card) / 2);transition:background .16s ease,color .16s ease,border-color .16s ease;}' +
      '.arl-size:hover:not([aria-disabled=\'true\']){border-color:var(--arl-text-2);}' +
      ".arl-size[aria-checked='true']{background:var(--arl-accent);color:var(--arl-on-accent);border-color:var(--arl-accent);}" +
      ".arl-size[aria-disabled='true']{opacity:.4;cursor:not-allowed;text-decoration:line-through;}" +
      '.arl-size:focus-visible{outline:2px solid var(--arl-accent);outline-offset:2px;}';
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = css;
    (document.head || document.documentElement).appendChild(el);
  }

  function handleFromUrl(url) {
    if (!url) return null;
    var clean = String(url).split('?')[0].split('#')[0];
    var m = clean.match(/\/products\/([^\/]+)\/?$/);
    return m ? m[1] : null;
  }

  function fetchVariants(handle, cb) {
    if (!handle) { cb(null); return; }
    if (cache[handle]) { cb(cache[handle]); return; }
    if (pending[handle]) { cb(null); return; }
    pending[handle] = true;
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    fetch(root.replace(/\/$/, '') + '/products/' + handle + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        pending[handle] = false;
        if (!p || !p.variants) { cache[handle] = []; cb(cache[handle]); return; }
        cache[handle] = p.variants.map(function (v) {
          return {
            id: String(v.id),
            title: v.title,
            available: v.available !== false
          };
        });
        cb(cache[handle]);
      })
      .catch(function () {
        pending[handle] = false;
        cache[handle] = [];
        cb(cache[handle]);
      });
  }

  function poolItems(card) {
    var pool = card.querySelector('[data-arl-pool]');
    return pool ? [].slice.call(pool.querySelectorAll('[data-arl-pool-item]')) : [];
  }

  function activeItem(card) {
    if (card.__arlActive && card.__arlActive.isConnected) return card.__arlActive;
    var checked = card.querySelector('[data-arl-pool-item][aria-checked="true"]');
    if (checked) return checked;
    var items = poolItems(card);
    return items.length ? items[0] : null;
  }

  function variantsFor(item) {
    if (!item) return null;
    var h = handleFromUrl(item.getAttribute('data-url'));
    return h ? cache[h] : null;
  }

  function activeIsMulti(card) {
    var v = variantsFor(activeItem(card));
    return !!(v && v.length > 1);
  }

  function ensureSizeRow(card) {
    var row = card.querySelector('.arl-sizes');
    if (row) return row;
    row = document.createElement('div');
    row.className = 'arl-sizes';
    row.setAttribute('data-arl-sizes', '');
    row.setAttribute('role', 'radiogroup');
    row.setAttribute('aria-label', 'Choose size');
    row.hidden = true;
    var anchor = card.querySelector('.arl-gift__row');
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(row, anchor.nextSibling);
    else card.appendChild(row);
    return row;
  }

  function renderChips(card, row, variants) {
    var sig = variants
      .map(function (v) { return v.id + ':' + (v.available ? '1' : '0'); })
      .join('|');
    if (row.getAttribute('data-sig') === sig) return; /* unchanged - keep selection */
    row.setAttribute('data-sig', sig);
    row.innerHTML = '';
    var label = document.createElement('span');
    label.className = 'arl-sizes__label';
    label.textContent = 'Choose size';
    row.appendChild(label);
    variants.forEach(function (v) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'arl-size';
      b.setAttribute('role', 'radio');
      b.setAttribute('data-variant-id', v.id);
      b.setAttribute('data-title', v.title);
      b.setAttribute('aria-checked', 'false');
      if (!v.available) b.setAttribute('aria-disabled', 'true');
      b.textContent = v.title;
      row.appendChild(b);
    });
    card.__arlChosenId = null;
  }

  function setChosen(card, id, title) {
    card.__arlChosenId = id;
    card.__arlChosenTitle = title;
    var row = card.querySelector('.arl-sizes');
    if (!row) return;
    [].forEach.call(row.querySelectorAll('.arl-size'), function (c) {
      c.setAttribute('aria-checked', c.getAttribute('data-variant-id') === id ? 'true' : 'false');
    });
  }

  function enhance(card) {
    if (!card || applying) return;
    var items = poolItems(card);
    if (!items.length) return;

    /* Prefetch every pool product's variants so click interception can decide
       synchronously later. */
    items.forEach(function (it) {
      var h = handleFromUrl(it.getAttribute('data-url'));
      if (h && !cache[h] && !pending[h]) fetchVariants(h, function () { schedule(); });
    });

    var active = activeItem(card);
    var variants = variantsFor(active);
    if (!variants) return; /* not fetched yet - a later pass will handle it */

    var state = unlockedState();
    var row = card.querySelector('.arl-sizes');

    applying = true;
    try {
      if (variants.length <= 1) {
        /* Single-variant gift: nothing to choose. Leave the native path alone. */
        if (row) row.hidden = true;
        card.__arlChosenId = null;
      } else {
        row = ensureSizeRow(card);
        renderChips(card, row, variants);
        row.hidden = !(state.unlocked && !state.inCart);

        var claim = card.querySelector('[data-arl-claim]');
        if (claim && state.unlocked && !state.inCart) {
          if (card.__arlChosenId) {
            var claimTxt = copy('claim', 'Claim');
            if (claim.textContent !== claimTxt) claim.textContent = claimTxt;
            claim.disabled = false;
          } else {
            var chooseTxt = 'Choose size';
            if (claim.textContent !== chooseTxt) claim.textContent = chooseTxt;
            claim.disabled = false;
          }
        }
      }

      /* Suppress auto-add only while the FEATURED (first) gift is multi-variant,
         so a default size is never added without a choice. */
      var featuredVariants = variantsFor(items[0]);
      if (
        featuredVariants &&
        featuredVariants.length > 1 &&
        state.unlocked &&
        !state.inCart &&
        window.__arlInstance
      ) {
        window.__arlInstance.autoAddDone = true;
      }
    } catch (e) {
    } finally {
      applying = false;
    }
  }

  function enhanceAll() {
    rafId = null;
    var cards = document.querySelectorAll('[data-arl-gift]');
    for (var i = 0; i < cards.length; i++) {
      try { enhance(cards[i]); } catch (e) {}
    }
  }

  function schedule() {
    if (rafId) return;
    if (window.requestAnimationFrame) rafId = window.requestAnimationFrame(enhanceAll);
    else rafId = setTimeout(enhanceAll, 60);
  }

  /* Clicks - capture phase, so we run before cart-rewards.js's own handlers and
     can stop them for the multi-variant case. */
  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      var chip = t.closest('.arl-size');
      if (chip) {
        if (chip.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
        var chipCard = chip.closest('[data-arl-gift]');
        if (chipCard) setChosen(chipCard, chip.getAttribute('data-variant-id'), chip.getAttribute('data-title'));
        schedule();
        return;
      }

      var claim = t.closest('[data-arl-claim]');
      if (claim) {
        var claimCard = claim.closest('[data-arl-gift]');
        if (claimCard && activeIsMulti(claimCard)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (claimCard.__arlChosenId && window.__arlInstance) {
            window.__arlInstance.addGift(claimCard.__arlChosenId, claimCard.__arlChosenTitle, 'claim');
          } else {
            var row = claimCard.querySelector('.arl-sizes');
            if (row) row.hidden = false;
          }
        }
        return; /* single-variant: let the native handler add it */
      }

      var item = t.closest('[data-arl-pool-item]');
      if (item && !item.disabled) {
        var itemCard = item.closest('[data-arl-gift]');
        var v = variantsFor(item);
        if (itemCard && v && v.length > 1) {
          /* Multi-variant gift chosen from the pool: make it the featured gift
             and let the customer pick a size instead of adding a default one. */
          e.preventDefault();
          e.stopImmediatePropagation();
          itemCard.__arlActive = item;
          itemCard.__arlChosenId = null;
          var titleEl = itemCard.querySelector('[data-arl-gift-title]');
          if (titleEl) titleEl.textContent = item.getAttribute('data-product-title') || titleEl.textContent;
          var imgEl = itemCard.querySelector('img.arl-gift__img');
          var imgSrc = item.getAttribute('data-image');
          if (imgEl && imgSrc) { imgEl.removeAttribute('srcset'); imgEl.setAttribute('src', imgSrc); }
          schedule();
        }
        /* single-variant: let the native handler add it */
      }
    },
    true
  );

  var mo = new MutationObserver(function () {
    if (applying) return;
    schedule();
  });
  try {
    mo.observe(document.body, { childList: true, subtree: true });
  } catch (e) {}

  document.addEventListener('shopify:section:load', schedule);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { injectStyle(); schedule(); }, { once: true });
  } else {
    injectStyle();
    schedule();
  }
  injectStyle();
})();
/* ==========================================================================
   Cart Rewards - free-gift CLAIM POPUP  (append to collapse module)
   --------------------------------------------------------------------------
   Centred modal that surfaces the unlocked free gift so the customer sees it,
   chooses gift + size, and claims it. Centred on ALL viewports with safe
   margins + internal scroll, so it never crops on mobile.

   STYLING: inherits the live theme.
   - Colours + corner radius copied from the rendered .arl-root (--arl-*), so it
     matches the reward card the merchant configured, on either store.
   - HEADING font = the theme's heading font; BODY font = the theme's body font,
     both read at runtime (no hard-coded typeface).

   Layout:
   - "GIFT UNLOCKED" is the big green headline (heading font).
   - Grey tile frames the gift(s) with an even 6px margin.
   - 1 gift  -> single framed preview.
   - 2 gifts -> shown side by side; selected outlined, the other under a white
     cast; tap to switch. Size options follow the selected gift.
   - Everything below the tile is centre-aligned.

   Behaviour: auto-fires once per session when unlocked + drawer open; CLAIM-ONLY
   (suppresses the ladder auto-add); claims via window.__arlInstance.addGift().
   Fully try/catch-guarded; no-ops without the ladder. Still relies on the gift
   discount zeroing the item at checkout.
   ========================================================================== */
(function () {
  'use strict';

  var SS_KEY = 'arlGiftPopupShown';
  var STYLE_ID = 'arl-gp-style';
  var cache = {};
  var pendingF = {};
  var overlay = null;
  var gifts = [];
  var st = { sel: 0, size: null, sizeTitle: null };
  var rafId = null;

  function inst() { return window.__arlInstance || null; }

  function ladderState() {
    try {
      var s = inst() && inst().lastState;
      return { unlocked: !!(s && s.giftUnlocked), inCart: !!(s && s.giftInCart) };
    } catch (e) { return { unlocked: false, inCart: false }; }
  }

  function drawerOpen() {
    return !!document.querySelector(
      'cart-drawer.modal--open, cart-drawer[open], .cart-drawer.modal--open, .cart-drawer.active, .cart-drawer.is-open, #CartDrawer.modal--open, #CartDrawer[open], .drawer--cart.active'
    );
  }

  function shownAlready() { try { return sessionStorage.getItem(SS_KEY) === '1'; } catch (e) { return false; } }
  function markShown() { try { sessionStorage.setItem(SS_KEY, '1'); } catch (e) {} }

  function handleFromUrl(url) {
    if (!url) return null;
    var c = String(url).split('?')[0].split('#')[0];
    var m = c.match(/\/products\/([^\/]+)\/?$/);
    return m ? m[1] : null;
  }

  function bigImg(url) {
    if (!url) return '';
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1440');
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=440';
  }

  function fetchVariants(handle, cb) {
    if (!handle) { cb(null); return; }
    if (cache[handle]) { cb(cache[handle]); return; }
    if (pendingF[handle]) { cb(null); return; }
    pendingF[handle] = true;
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
    fetch(base.replace(/\/$/, '') + '/products/' + handle + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        pendingF[handle] = false;
        cache[handle] = (p && p.variants) ? p.variants.map(function (v) { return { id: String(v.id), title: v.title, available: v.available !== false }; }) : [];
        cb(cache[handle]);
      })
      .catch(function () { pendingF[handle] = false; cache[handle] = []; cb(cache[handle]); });
  }

  function readPool() {
    var card = document.querySelector('[data-arl-gift]');
    if (!card) return [];
    var items = [].slice.call(card.querySelectorAll('[data-arl-pool-item]'));
    if (items.length) {
      return items.map(function (it) {
        return {
          name: it.getAttribute('data-product-title') || 'Free gift',
          handle: handleFromUrl(it.getAttribute('data-url')),
          image: it.getAttribute('data-image') || '',
          defaultId: it.getAttribute('data-variant-id') || null,
          variants: null
        };
      });
    }
    var claim = card.querySelector('[data-arl-claim]');
    if (!claim) return [];
    var title = card.querySelector('[data-arl-gift-title]');
    var img = card.querySelector('img.arl-gift__img');
    return [{
      name: (title && title.textContent.trim()) || 'Your free gift',
      handle: null,
      image: (img && img.getAttribute('src')) || '',
      defaultId: claim.getAttribute('data-variant-id') || null,
      variants: null
    }];
  }

  function cssVar(cs, name, fb) { var x = cs ? (cs.getPropertyValue(name) || '').trim() : ''; return x || fb; }

  function themeVars() {
    var probe = document.querySelector('.arl-root');
    var cs = probe ? getComputedStyle(probe) : null;
    return {
      bg: cssVar(cs, '--arl-bg', '#f3f3f3'),
      text: cssVar(cs, '--arl-text', '#000000'),
      text2: cssVar(cs, '--arl-text-2', '#666666'),
      accent: cssVar(cs, '--arl-accent', '#00ae30'),
      fill: cssVar(cs, '--arl-fill', '#1ccc4d'),
      onAccent: cssVar(cs, '--arl-on-accent', '#04210f'),
      rad: cssVar(cs, '--arl-radius-card', '2px')
    };
  }

  function bodyFont() {
    try { return getComputedStyle(document.body).fontFamily || 'inherit'; } catch (e) { return 'inherit'; }
  }

  function headingFont() {
    try {
      var r = getComputedStyle(document.documentElement);
      var names = ['--font-heading-family', '--heading-font-family', '--font-heading', '--type-header-font-family', '--font-header-family'];
      for (var i = 0; i < names.length; i++) { var x = (r.getPropertyValue(names[i]) || '').trim(); if (x) return x; }
      var el = document.querySelector('h1, h2, h3, .h1, .h2, .h3, .heading, [class*="__heading"], [class*="title--"]');
      if (el) { var f = getComputedStyle(el).fontFamily; if (f) return f; }
    } catch (e) {}
    return bodyFont();
  }

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var css =
      '.arl-gp{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(0,0,0,.55);opacity:0;transition:opacity .28s ease;font-family:var(--gp-bfont,inherit);overflow:auto;-webkit-overflow-scrolling:touch;}' +
      '.arl-gp[data-open="true"]{opacity:1;}' +
      '.arl-gp__sheet{position:relative;width:100%;max-width:420px;max-height:calc(100vh - 32px);overflow:auto;-webkit-overflow-scrolling:touch;margin:auto;background:#ffffff;color:var(--gp-text);border-radius:var(--gp-rad);padding:22px 20px 20px;transform:translateY(12px) scale(.97);transition:transform .34s cubic-bezier(.22,.61,.36,1);}' +
      '.arl-gp[data-open="true"] .arl-gp__sheet{transform:translateY(0) scale(1);}' +
      '.arl-gp__x{position:absolute;top:14px;right:14px;width:28px;height:28px;display:grid;place-items:center;background:transparent;border:0;color:var(--gp-text2);cursor:pointer;font-size:20px;line-height:1;z-index:2;}' +
      '.arl-gp__line{display:block;height:3px;width:100%;background:var(--gp-fill);margin-bottom:16px;}' +
      '.arl-gp__head{display:flex;align-items:center;gap:9px;margin-bottom:6px;}' +
      '.arl-gp__head i{font-size:26px;color:var(--gp-accent);}' +
      '.arl-gp__title{font-family:var(--gp-hfont,inherit);font-size:26px;font-weight:600;letter-spacing:.03em;color:var(--gp-accent);text-transform:uppercase;line-height:1;}' +
      '.arl-gp__sub{margin:0 0 16px;font-size:12px;line-height:1.5;color:var(--gp-text2);}' +
      '.arl-gp__tile{background:var(--gp-bg);border-radius:var(--gp-rad);padding:6px;display:flex;gap:6px;margin-bottom:16px;}' +
      '.arl-gp__cell{position:relative;flex:1;min-width:0;background:#fff;border:2px solid #e2e2e2;border-radius:var(--gp-rad);padding:12px 8px;display:flex;flex-direction:column;align-items:center;gap:8px;text-align:center;cursor:pointer;transition:border-color .14s ease;}' +
      '.arl-gp__cell[aria-checked="true"]{border-color:var(--gp-accent);}' +
      '.arl-gp__cell[data-single="true"]{cursor:default;}' +
      '.arl-gp__cast{position:absolute;inset:0;background:rgba(255,255,255,.6);pointer-events:none;border-radius:var(--gp-rad);}' +
      '.arl-gp__cimg{max-width:100%;object-fit:contain;display:block;}' +
      '.arl-gp__cimg--ph{display:grid;place-items:center;color:var(--gp-text);}' +
      '.arl-gp__cname{font-weight:500;letter-spacing:.05em;text-transform:uppercase;color:var(--gp-text);line-height:1.2;}' +
      '.arl-gp__free{font-size:10px;letter-spacing:.16em;font-weight:500;text-transform:uppercase;color:var(--gp-on);background:var(--gp-fill);padding:3px 9px;border-radius:var(--gp-rad);}' +
      '.arl-gp__sizes{margin-bottom:16px;text-align:center;}' +
      '.arl-gp__sizes[hidden]{display:none;}' +
      '.arl-gp__slabel{display:block;font-size:10px;letter-spacing:.16em;color:var(--gp-text2);text-transform:uppercase;margin-bottom:8px;}' +
      '.arl-gp__chips{display:flex;flex-wrap:wrap;gap:7px;justify-content:center;}' +
      '.arl-gp__chip{min-width:44px;font-family:inherit;font-size:12px;padding:9px 12px;border-radius:var(--gp-rad);border:1px solid rgba(0,0,0,.2);background:#fff;color:var(--gp-text);font-weight:500;cursor:pointer;transition:all .14s ease;}' +
      '.arl-gp__chip[aria-checked="true"]{border-color:var(--gp-accent);background:var(--gp-fill);color:var(--gp-on);}' +
      '.arl-gp__chip[aria-disabled="true"]{color:#bbb;cursor:not-allowed;text-decoration:line-through;}' +
      '.arl-gp__hint{margin:8px 0 0;font-size:11px;color:var(--gp-accent);display:none;}' +
      '.arl-gp__claim{width:100%;font-family:inherit;padding:15px;border:0;border-radius:var(--gp-rad);font-size:12px;letter-spacing:.16em;text-transform:uppercase;font-weight:500;display:inline-flex;align-items:center;justify-content:center;gap:8px;background:var(--gp-fill);color:var(--gp-on);cursor:pointer;transition:background .16s ease,transform .12s ease;}' +
      '.arl-gp__claim:active{transform:scale(.99);}' +
      '.arl-gp__claim[data-ready="false"]{background:#e2e2e2;color:#999;}' +
      '.arl-gp__later{width:100%;margin-top:10px;padding:6px;background:transparent;border:0;color:var(--gp-text2);font-size:11px;letter-spacing:.08em;text-transform:uppercase;cursor:pointer;font-family:inherit;text-align:center;}' +
      '.arl-gp__ok{text-align:center;padding:20px 0 6px;}' +
      '.arl-gp__okdot{width:60px;height:60px;background:var(--gp-fill);display:grid;place-items:center;color:var(--gp-on);margin:0 auto 16px;border-radius:var(--gp-rad);font-size:32px;animation:arl-gp-pop .5s cubic-bezier(.22,.61,.36,1) both;}' +
      '.arl-gp__oktitle{font-family:var(--gp-hfont,inherit);font-size:18px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--gp-text);margin-bottom:8px;}' +
      '.arl-gp__okline{font-size:12px;color:var(--gp-accent);letter-spacing:.03em;margin-bottom:20px;}' +
      '.arl-gp__keep{width:100%;font-family:inherit;padding:14px;border:1px solid var(--gp-text);border-radius:var(--gp-rad);font-size:12px;letter-spacing:.16em;text-transform:uppercase;font-weight:500;background:var(--gp-text);color:#fff;cursor:pointer;}' +
      '.arl-gp__conf{position:absolute;top:40px;width:6px;height:6px;opacity:0;z-index:3;}' +
      '@keyframes arl-gp-pop{0%{transform:scale(.4);opacity:0}60%{transform:scale(1.12)}100%{transform:scale(1);opacity:1}}' +
      '@keyframes arl-gp-fall{0%{transform:translateY(-10px) rotate(0);opacity:1}100%{transform:translateY(240px) rotate(300deg);opacity:0}}' +
      '@keyframes arl-gp-shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-5px)}40%,80%{transform:translateX(5px)}}';
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = css;
    (document.head || document.documentElement).appendChild(el);
  }

  function igift() { return '<i class="ti ti-gift" aria-hidden="true"></i>'; }

  function cellHtml(g, i, single) {
    var selected = st.sel === i;
    var imgBox = single ? 150 : 104;
    var iconSize = single ? 74 : 54;
    var media = g.image
      ? '<img class="arl-gp__cimg" src="' + bigImg(g.image) + '" alt="" style="width:' + imgBox + 'px;height:' + imgBox + 'px">'
      : '<span class="arl-gp__cimg arl-gp__cimg--ph" style="width:' + imgBox + 'px;height:' + imgBox + 'px;font-size:' + iconSize + 'px">' + igift() + '</span>';
    var cast = (!single && !selected) ? '<span class="arl-gp__cast"></span>' : '';
    return '<div class="arl-gp__cell" data-gp-cell="' + i + '" role="radio" aria-checked="' + (selected ? 'true' : 'false') + '"' + (single ? ' data-single="true"' : '') + '>' +
      media +
      '<span class="arl-gp__cname" style="font-size:' + (single ? 14 : 12) + 'px">' + g.name + '</span>' +
      '<span class="arl-gp__free">Free</span>' +
      cast +
      '</div>';
  }

  function render() {
    if (!overlay) return;
    if (st.sel >= gifts.length) st.sel = 0;
    var single = gifts.length === 1;
    var cells = gifts.map(function (g, i) { return cellHtml(g, i, single); }).join('');
    var g = gifts[st.sel];
    var sizeBlock = '';
    if (g && g.variants && g.variants.length > 1) {
      sizeBlock = '<div class="arl-gp__sizes"><span class="arl-gp__slabel">Select size</span><div class="arl-gp__chips" data-gp-chips></div><p class="arl-gp__hint">Pick a size to claim your gift.</p></div>';
    }
    var body = overlay.querySelector('.arl-gp__body');
    body.innerHTML =
      '<span class="arl-gp__line"></span>' +
      '<div class="arl-gp__head">' + igift() + '<span class="arl-gp__title">Gift unlocked</span></div>' +
      '<p class="arl-gp__sub">You\'ve unlocked a free gift \u2014 it\'s on us.</p>' +
      '<div class="arl-gp__tile">' + cells + '</div>' +
      sizeBlock +
      '<button class="arl-gp__claim" data-gp-claim data-ready="false">Claim my free gift ' + igift() + '</button>' +
      '<button class="arl-gp__later" data-gp-later>No thanks, maybe later</button>';

    if (g && g.variants && g.variants.length > 1) {
      var chips = body.querySelector('[data-gp-chips]');
      g.variants.forEach(function (v) {
        var b = document.createElement('button');
        b.className = 'arl-gp__chip';
        b.setAttribute('type', 'button');
        b.setAttribute('data-gp-chip', v.id);
        b.setAttribute('data-title', v.title);
        b.setAttribute('aria-checked', st.size === v.id ? 'true' : 'false');
        if (!v.available) b.setAttribute('aria-disabled', 'true');
        b.textContent = v.title;
        chips.appendChild(b);
      });
    }
    paintClaim();
  }

  function chosenVariant() {
    var g = gifts[st.sel];
    if (!g) return null;
    var multi = g.variants && g.variants.length > 1;
    if (multi) return st.size ? { id: st.size, title: st.sizeTitle } : null;
    if (g.variants && g.variants.length === 1) return { id: g.variants[0].id, title: g.variants[0].title };
    return g.defaultId ? { id: g.defaultId, title: '' } : null;
  }

  function paintClaim() {
    var btn = overlay.querySelector('.arl-gp__claim');
    if (btn) btn.setAttribute('data-ready', chosenVariant() ? 'true' : 'false');
  }

  function loadSizes(afterSel) {
    var g = gifts[st.sel];
    if (!g) return;
    if (g.variants) { render(); return; }
    if (!g.handle) { g.variants = g.defaultId ? [{ id: g.defaultId, title: '', available: true }] : []; render(); return; }
    fetchVariants(g.handle, function (vs) {
      g.variants = vs || (g.defaultId ? [{ id: g.defaultId, title: '', available: true }] : []);
      render();
    });
  }

  function selectGift(i) {
    if (i == null || isNaN(i) || i === st.sel) return;
    st.sel = i; st.size = null; st.sizeTitle = null;
    loadSizes();
  }

  function doClaim() {
    var g = gifts[st.sel];
    var cv = chosenVariant();
    if (!cv) {
      var wrap = overlay.querySelector('.arl-gp__sizes');
      var hint = overlay.querySelector('.arl-gp__hint');
      if (hint) hint.style.display = 'block';
      if (wrap) { wrap.style.animation = 'arl-gp-shake .4s'; setTimeout(function () { wrap.style.animation = ''; }, 420); }
      return;
    }
    try { if (inst() && typeof inst().addGift === 'function') inst().addGift(cv.id, g ? g.name : '', 'claim'); } catch (e) {}
    success(g, cv);
  }

  function success(g, cv) {
    var body = overlay.querySelector('.arl-gp__body');
    var name = (g && g.name) || 'Your free gift';
    var line = name + (cv && cv.title ? ' \u00b7 ' + cv.title : '') + ' \u00b7 Free';
    body.innerHTML =
      '<div class="arl-gp__ok">' +
        '<div class="arl-gp__okdot"><i class="ti ti-check" aria-hidden="true"></i></div>' +
        '<div class="arl-gp__oktitle">Added to your cart</div>' +
        '<div class="arl-gp__okline">' + line + '</div>' +
        '<button class="arl-gp__keep" data-gp-keep>Keep shopping</button>' +
      '</div>';
    confetti();
    markShown();
  }

  function confetti() {
    var sheet = overlay.querySelector('.arl-gp__sheet');
    var tv = themeVars();
    var cols = [tv.fill, tv.accent, tv.text];
    for (var i = 0; i < 24; i++) {
      var p = document.createElement('span');
      p.className = 'arl-gp__conf';
      p.style.left = (8 + Math.random() * 84) + '%';
      p.style.background = cols[i % 3];
      p.style.animation = 'arl-gp-fall ' + (0.9 + Math.random() * 0.6).toFixed(2) + 's ease-in ' + (Math.random() * 0.22).toFixed(2) + 's forwards';
      sheet.appendChild(p);
      (function (el) { setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 2200); })(p);
    }
  }

  function onClick(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t === overlay) { dismiss(); return; }
    if (t.closest('.arl-gp__x')) { dismiss(); return; }
    if (t.closest('[data-gp-later]')) { dismiss(); return; }
    if (t.closest('[data-gp-keep]')) { close(); return; }
    var cell = t.closest('[data-gp-cell]');
    if (cell) { selectGift(parseInt(cell.getAttribute('data-gp-cell'), 10)); return; }
    var chip = t.closest('[data-gp-chip]');
    if (chip) {
      if (chip.getAttribute('aria-disabled') === 'true') return;
      st.size = chip.getAttribute('data-gp-chip');
      st.sizeTitle = chip.getAttribute('data-title');
      render();
      return;
    }
    if (t.closest('[data-gp-claim]')) { doClaim(); return; }
  }

  function onKey(e) { if (overlay && e.key === 'Escape') dismiss(); }

  function build() {
    injectStyle();
    var tv = themeVars();
    overlay = document.createElement('div');
    overlay.className = 'arl-gp';
    overlay.setAttribute('data-open', 'false');
    overlay.style.setProperty('--gp-bg', tv.bg);
    overlay.style.setProperty('--gp-text', tv.text);
    overlay.style.setProperty('--gp-text2', tv.text2);
    overlay.style.setProperty('--gp-accent', tv.accent);
    overlay.style.setProperty('--gp-fill', tv.fill);
    overlay.style.setProperty('--gp-on', tv.onAccent);
    overlay.style.setProperty('--gp-rad', tv.rad);
    overlay.style.setProperty('--gp-hfont', headingFont());
    overlay.style.setProperty('--gp-bfont', bodyFont());
    overlay.innerHTML =
      '<div class="arl-gp__sheet" role="dialog" aria-modal="true" aria-label="Claim your free gift">' +
        '<button class="arl-gp__x" aria-label="Close"><i class="ti ti-x" aria-hidden="true"></i></button>' +
        '<div class="arl-gp__body"></div>' +
      '</div>';
    document.body.appendChild(overlay);
    overlay.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
  }

  function open() {
    if (overlay) return;
    gifts = readPool();
    if (!gifts.length) return;
    st = { sel: 0, size: null, sizeTitle: null };
    build();
    markShown();
    try { document.documentElement.style.overflow = 'hidden'; } catch (e) {}
    render();
    loadSizes();
    requestAnimationFrame(function () { requestAnimationFrame(function () { if (overlay) overlay.setAttribute('data-open', 'true'); }); });
  }

  function close() {
    if (!overlay) return;
    var o = overlay;
    o.setAttribute('data-open', 'false');
    document.removeEventListener('keydown', onKey);
    try { document.documentElement.style.overflow = ''; } catch (e) {}
    overlay = null;
    setTimeout(function () { if (o && o.parentNode) o.parentNode.removeChild(o); }, 400);
  }

  function dismiss() { markShown(); close(); }

  function tick() {
    rafId = null;
    var s = ladderState();
    if (s.unlocked && inst()) { try { inst().autoAddDone = true; } catch (e) {} }
    if (!overlay && !shownAlready() && s.unlocked && !s.inCart && drawerOpen()) open();
  }

  function schedule() {
    if (rafId) return;
    rafId = window.requestAnimationFrame ? window.requestAnimationFrame(tick) : setTimeout(tick, 120);
  }

  try {
    var mo = new MutationObserver(function () { schedule(); });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'open'] });
  } catch (e) {}
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('cart:updated', schedule);
  document.addEventListener('cart:refresh', schedule);
  schedule();
})();